import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * WeatherChip — live-weather glass pill, designed to float over the hero
 * photograph on the homepage.
 *
 * - Pulls cached weather from the backend (which itself caches Open-Meteo
 *   for 15 minutes), so render cost is effectively zero.
 * - Auto-refreshes every 10 minutes while the tab is open.
 * - Hover/tap shows wind & humidity details.
 * - Renders nothing on failure — deliberately silent.
 */
const WeatherChip = () => {
  const [w, setW] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetch = () =>
      axios
        .get(`${API}/api/weather`)
        .then((r) => alive && setW(r.data))
        .catch(() => alive && setW(null));
    fetch();
    const t = setInterval(fetch, 10 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!w) return null;

  return (
    <div
      className="relative flex items-center gap-2 px-3.5 py-2 rounded-2xl glass-strong text-xs font-medium text-slate-800 cursor-default select-none shadow-lg shadow-slate-900/10"
      onMouseEnter={() => setShowDetail(true)}
      onMouseLeave={() => setShowDetail(false)}
      onClick={() => setShowDetail((v) => !v)}
      title={`${w.label} in Paisley`}
      data-testid="hero-weather-chip"
    >
      <span aria-hidden className="text-lg leading-none">{w.icon}</span>
      <div className="leading-tight">
        <p className="font-bold text-slate-900 tabular-nums text-sm">{w.temp_c}°C</p>
        <p className="text-[10px] text-slate-600 max-w-[110px] truncate">Paisley · {w.label}</p>
      </div>

      {showDetail && (
        <div className="absolute top-full right-0 mt-2 px-3.5 py-2.5 rounded-xl glass-strong shadow-lg text-[11px] text-slate-700 whitespace-nowrap z-50 ring-1 ring-slate-200/60">
          <p className="font-bold text-slate-900 text-xs mb-1.5 flex items-center gap-1.5">
            <span className="text-sm">{w.icon}</span>
            Paisley · {w.temp_c}°C
          </p>
          <p className="text-slate-600">{w.label}</p>
          <p className="text-slate-500 mt-1">
            Wind <span className="font-semibold text-slate-700">{w.wind_kmh} km/h {w.wind_dir}</span>
          </p>
          <p className="text-slate-500">
            Humidity <span className="font-semibold text-slate-700">{w.humidity}%</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default WeatherChip;
